import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FaFilter,
  FaColumns,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { GoPencil } from "react-icons/go";
import ModifyColumns from "../Tools/ModifyColumns";
import {
  AddTableFilterComponent,
  apiRequestBodyBuilder,
} from "./../config/tableHelper";
import { BiHide, BiSolidBulb } from "react-icons/bi";
import { RenderTable } from "../config/tableWrapper";
import { DEVICE_ADDTABLE_COLUMNS } from "../Components/AddTableCols";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Insights Skeleton styled to match the device card layout
function InsightsSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm w-full p-4 sm:p-5 space-y-4 animate-pulse">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="flex flex-col sm:flex-row items-start gap-4 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0"
        >
          <div className="w-12 h-12 rounded-xl bg-gray-200 shrink-0" />
          <div className="w-36 shrink-0 pt-2">
            <div className="h-4 bg-gray-200 rounded w-24" />
          </div>
          <div className="flex-grow pt-2 w-full space-y-2">
            <div className="h-3.5 bg-gray-200 rounded w-full" />
            <div
              className="h-3 bg-gray-100 rounded"
              style={{ width: `${65 + (item % 3) * 15}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper function to return icon, badge styling, and title color according to entity/device type
const getDeviceStyle = (entityName = "") => {
  const name = entityName.toLowerCase();
  if (
    name.includes("computer") ||
    name.includes("desktop") ||
    name.includes("laptop")
  ) {
    return {
      bgIcon: "bg-blue-50 text-blue-600 border border-blue-100",
      titleColor: "text-blue-600",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
    };
  } else if (name.includes("mobile") || name.includes("phone")) {
    return {
      bgIcon: "bg-emerald-50 text-emerald-600 border border-emerald-100",
      titleColor: "text-emerald-600",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
    };
  } else if (name.includes("tablet")) {
    return {
      bgIcon: "bg-amber-50 text-amber-600 border border-amber-100",
      titleColor: "text-amber-600",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
    };
  } else {
    return {
      bgIcon: "bg-indigo-50 text-indigo-600 border border-indigo-100",
      titleColor: "text-indigo-600",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    };
  }
};

const DeviceTable = ({
  initialDateRange,
  insightAvailable = false,
  PageType,
}) => {
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState(DEVICE_ADDTABLE_COLUMNS);

  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState([]);
  const [campaignNameInput, setCampaignNameInput] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Editable Insights State
  const [insightsArray, setInsightsArray] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [isEditingInsight, setIsEditingInsight] = useState(false);
  const [editedInsights, setEditedInsights] = useState([]);

  const columnRef = useRef(null);
  const filterRef = useRef(null);

  const fetchData = async (nameContains = "") => {
    setLoading(true);
    setUnfilteredData([]);

    let requestBody = apiRequestBodyBuilder(initialDateRange, {
      reporting_type: PageType,
    });

    if (nameContains) {
      requestBody.name_contains = nameContains;
    }

    const endpoint = nameContains
      ? "https://api.confidanto.com/device/filter"
      : "https://api.confidanto.com/device/data";

    try {
      const response = await axios.post(endpoint, requestBody);
      setUnfilteredData(response.data || []);
      setColumns(DEVICE_ADDTABLE_COLUMNS);
    } catch (error) {
      setUnfilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const [currentGroupType, setCurrencyGroupType] = useState("");

  const fetchGroups = async () => {
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange);

      const responseInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      if (!responseInfo.ok) {
        throw new Error(
          `Failed to fetch group info. Status: ${responseInfo.status}`,
        );
      }
      const groupType = await responseInfo.json();

      const typeForQuery =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");
      const TYPEQUERY =
        typeForQuery?.charAt(0).toUpperCase() + typeForQuery?.slice(1);
      setCurrencyGroupType(TYPEQUERY);

      requestBody.type = typeForQuery;

      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch groups. Status: ${response.status}`);
      }
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      setGroups([]);
    }
  };

  const handleApplyCampaignFilter = () => {
    fetchData(campaignNameInput);
    setShowFilterDropdown(false);
    setSelectedFilter(null);

    const newFilter = {
      id: Date.now(),
      type: "campaign",
      value: campaignNameInput,
      label: `Campaign: ${campaignNameInput}`,
    };
    setAppliedFilters([newFilter]);
  };

  const handleGroupApply = async (remainingGroups = null) => {
    setLoading(true);
    setUnfilteredData([]);
    setShowFilterDropdown(false);
    setSelectedFilter(null);

    const activeGroups =
      remainingGroups !== null ? remainingGroups : selectedGroups;
    const selectedGroupIds = groups
      .filter((g) => activeGroups.includes(g.group_name))
      .map((g) => g.group_id);

    if (activeGroups.length === 0) {
      alert("Please select at least one group.");
      setLoading(false);
      return;
    }

    const groupFilters = activeGroups.map((group) => ({
      id: group,
      type: "groupwise",
      value: group,
      label: group,
    }));
    setAppliedFilters(groupFilters);

    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange, {
        group_ids: selectedGroupIds,
      });

      const response = await axios.post(
        "https://api.confidanto.com/device-group/datewise-data",
        requestBody,
      );

      const responseData = response.data;
      const formatted = responseData.map((item) => ({ ...item }));

      setUnfilteredData(formatted);
      setColumns((prevColumns) =>
        prevColumns.map((col) =>
          col.key === "device"
            ? { ...col, title: "Group", key: "group_name", keyType: "string" }
            : col,
        ),
      );
    } catch (error) {
      console.error("Groupwise Device Error: ", error);
      setUnfilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyStaticFilter = (filter) => {
    setAppliedFilters((prev) => [...prev, filter]);
    setShowFilterDropdown(false);
  };

  const handleRemoveFilter = async (filterToRemove) => {
    const newFilters = appliedFilters.filter((f) => f.id !== filterToRemove.id);

    if (filterToRemove.type === "groupwise") {
      const remainingGroups = newFilters
        .filter((f) => f.type === "groupwise")
        .map((f) => f.value);
      setSelectedGroups(remainingGroups);

      if (remainingGroups.length === 0) {
        setAppliedFilters([]);
        fetchData();
      } else {
        handleGroupApply(remainingGroups);
      }
    } else {
      setAppliedFilters(newFilters);
      if (filterToRemove.type === "campaign" && newFilters.length === 0) {
        fetchData();
      }
    }
  };

  const getFilteredData = () => {
    const staticFilters = appliedFilters.filter((f) => f.type === "static");
    if (staticFilters.length === 0) {
      return unfilteredData;
    }

    return unfilteredData.filter((row) => {
      return staticFilters.every((filter) => {
        const rowValue =
          typeof row[filter.key] === "object"
            ? row[filter.key].value
            : row[filter.key];

        if (rowValue === null || rowValue === undefined) return false;

        if (filter.keyType === "number") {
          const rowNum = parseFloat(rowValue);
          const filterNum = parseFloat(filter.value);
          if (isNaN(rowNum) || isNaN(filterNum)) return false;
          switch (filter.comparison) {
            case ">":
              return rowNum > filterNum;
            case "<":
              return rowNum < filterNum;
            case "=":
              return rowNum === filterNum;
            default:
              return true;
          }
        } else if (filter.keyType === "boolean") {
          return filter.value.includes(String(rowValue));
        } else {
          return rowValue
            .toString()
            .toLowerCase()
            .includes(filter.value.toLowerCase());
        }
      });
    });
  };

  const filteredData = getFilteredData();

  useEffect(() => {
    fetchGroups();
    fetchData();

    setAppliedFilters([]);
    setInsightsArray([]);
    setShowInsights(false);
    setIsEditingInsight(false);

    try {
      const currentStored = JSON.parse(
        localStorage.getItem("customrep_insight") || "{}",
      );
      currentStored["device"] = false;
      localStorage.setItem("customrep_insight", JSON.stringify(currentStored));
    } catch (e) {
      console.error(e);
    }
  }, [
    initialDateRange[0].startDate,
    initialDateRange[0].endDate,
    initialDateRange.length,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setShowColumnsMenu(false);
      }
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      ) {
        setShowFilterDropdown(false);
        setSelectedFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchInsights = async () => {
    setInsightsLoading(true);
    setInsightsArray([]);

    let requestBody = apiRequestBodyBuilder(initialDateRange, {
      insight_type: "device",
    });

    axios
      .post(
        "https://api.confidanto.com/get-insights/custom-reporting-insights",
        requestBody,
      )
      .then((res) => {
        setInsightsArray(res.data);
      })
      .catch((err) => console.error("Error Insights Device: ", err))
      .finally(() => {
        setInsightsLoading(false);
      });
  };

  const handleToggleInsights = () => {
    if (!showInsights && insightsArray.length === 0) {
      fetchInsights();
    }
    const newShowInsights = !showInsights;
    setShowInsights(newShowInsights);

    try {
      const currentStored = JSON.parse(
        localStorage.getItem("customrep_insight") || "{}",
      );
      currentStored["device"] = newShowInsights;
      localStorage.setItem("customrep_insight", JSON.stringify(currentStored));
    } catch (e) {
      console.error("Failed to save insight state", e);
    }
  };

  const updateInsight = async (newInsights) => {
    try {
      const baseRequestInfo = apiRequestBodyBuilder(initialDateRange, {});

      const updatePromises = newInsights.map((insight) => {
        const requestBody = {
          id: insight.id,
          ad_account_id: insight.ad_account_id || baseRequestInfo.ad_account_id,
          insight_type: insight.insight_type || "device",
          entity_name: insight.entity_name,
          insight_text: insight.insight_text,
        };

        return axios.post(
          "https://api.confidanto.com/get-insights/update-custom-reporting-insights",
          requestBody,
        );
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error updating insights: ", error);
      alert("Failed to save some insights. Please try again.");
    }
  };

  const handleEditClick = () => {
    setEditedInsights(insightsArray.map((insight) => ({ ...insight })));
    setIsEditingInsight(true);
  };

  const handleInsightChange = (index, newValue) => {
    const newArr = [...editedInsights];
    newArr[index] = { ...newArr[index], insight_text: newValue };
    setEditedInsights(newArr);
  };

  const handleSaveInsight = async () => {
    setInsightsLoading(true);
    await updateInsight(editedInsights);
    setInsightsArray(editedInsights);
    setIsEditingInsight(false);
    setInsightsLoading(false);
  };

  const activeInsightsData = isEditingInsight ? editedInsights : insightsArray;

  return (
    <div className="flex h-full bg-gray-100 font-roboto">
      <main className="w-full p-4">
        <div className="flex justify-between items-center mb-3 space-x-1.5">
          <div
            className={`transition-opacity duration-300 ${
              loading ? "pointer-events-none opacity-50" : ""
            }`}
          >
            <AddTableFilterComponent
              data={unfilteredData}
              filterRef={filterRef}
              showFilterDropdown={showFilterDropdown}
              setShowFilterDropdown={setShowFilterDropdown}
              appliedFilters={appliedFilters}
              handleRemoveFilter={handleRemoveFilter}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              onFetchGroups={fetchGroups}
              columns={columns}
              campaignNameInput={campaignNameInput}
              setCampaignNameInput={setCampaignNameInput}
              onApplyCampaignFilter={handleApplyCampaignFilter}
              groups={groups}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedGroups}
              onApplyGroupFilter={handleGroupApply}
              onApplyStaticFilter={handleApplyStaticFilter}
              currentGroupType={currentGroupType}
            />
          </div>
          <div className="flex space-x-1.5">
            <div className="relative" ref={columnRef}>
              <button
                disabled={loading}
                className={`bg-transparent text-gray-600 px-3 py-1.5 rounded flex flex-col items-center justify-center transition-all ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-100"
                }`}
                onClick={() => setShowColumnsMenu(!showColumnsMenu)}
              >
                <FaColumns className="text-sm" />
                <span className="text-sm">Columns</span>
              </button>
              {showColumnsMenu && (
                <ModifyColumns
                  columns={columns}
                  setColumns={setColumns}
                  setShowColumnsMenu={setShowColumnsMenu}
                  colsLimit={13}
                />
              )}
            </div>
          </div>
        </div>

        {/* Table & Skeleton Loading */}
        {loading ? (
          <div className="mt-3">
            <TableSkeleton />
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded shadow mt-3">
            {filteredData.length > 0 ? (
              <RenderTable
                columns={columns}
                data={filteredData}
                enableExpandCollapse={initialDateRange.length > 1}
              />
            ) : (
              <div className="flex justify-center items-center h-40 mt-6">
                <h2>No Data Found</h2>
              </div>
            )}
          </div>
        )}

        {insightAvailable && (
          <div className="w-full my-3">
            <button
              disabled={loading}
              onClick={handleToggleInsights}
              className={`flex items-center justify-center gap-1.5 w-auto bg-[#283593] text-white font-semibold py-1.5 px-3 rounded-lg shadow-md transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-75 ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#1a237e]"
              }`}
            >
              {showInsights ? (
                <>
                  <BiHide size="1.2em" />
                  <span className="text-sm">Hide Insights</span>
                </>
              ) : (
                <>
                  <BiSolidBulb size="1.2em" />
                  <span className="text-sm">Show Insights</span>
                </>
              )}
            </button>

            <div
              className={`w-full transition-all duration-500 ease-in-out overflow-hidden ${
                showInsights ? " opacity-100 mt-2" : "max-h-0 opacity-0"
              }`}
            >
              {insightsLoading ? (
                <InsightsSkeleton />
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm w-full relative overflow-hidden">
                  {!isEditingInsight && insightsArray.length > 0 && (
                    <button
                      onClick={handleEditClick}
                      className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full shadow hover:bg-blue-700 transition-transform hover:scale-105 z-10"
                      title="Edit Insights"
                    >
                      <GoPencil size={16} />
                    </button>
                  )}

                  {insightsArray.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {activeInsightsData.map((item, index) => {
                        const style = getDeviceStyle(item.entity_name);
                        return (
                          <div
                            key={index}
                            className="p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-4 hover:bg-slate-50/50 transition-colors"
                          >
                            {/* Device Icon Box */}
                            <div
                              className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${style.bgIcon}`}
                            >
                              {style.icon}
                            </div>

                            {/* Device Title */}
                            <div
                              className={`w-36 shrink-0 font-bold text-base pt-2.5 sm:pt-2 ${style.titleColor}`}
                            >
                              {item.entity_name}
                            </div>

                            {/* Device Text Summary or Textarea in edit mode */}
                            <div className="flex-grow text-xs sm:text-sm text-slate-600 leading-relaxed pt-2.5 sm:pt-2 w-full">
                              {isEditingInsight ? (
                                <div className="grid w-full">
                                  <textarea
                                    className="col-start-1 col-end-2 row-start-1 row-end-2 w-full resize-none overflow-hidden bg-white border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-md p-2 outline-none transition-all duration-200 text-slate-700 leading-relaxed"
                                    value={item.insight_text}
                                    onChange={(e) =>
                                      handleInsightChange(index, e.target.value)
                                    }
                                    rows={3}
                                  />
                                </div>
                              ) : (
                                <span>{item.insight_text}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-sm text-red-500">
                      No Insights found for above data
                    </div>
                  )}

                  {isEditingInsight && (
                    <div className="flex justify-end gap-3 p-4 bg-slate-50 border-t border-gray-100">
                      <button
                        onClick={() => setIsEditingInsight(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveInsight}
                        className="px-4 py-2 text-sm font-medium text-white bg-[#283593] border border-transparent rounded-lg shadow-sm hover:bg-[#1a237e] focus:outline-none transition-all"
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DeviceTable;
